/* About page motion: entrances, the statement that fills in word by word, drifting photos, the sliding photo
   strip and the counting figures. Everything is visible without this script; it only adds movement. */
(() => {
  if (window.rookAboutReady) return; // each About section includes this file
  window.rookAboutReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Links to a spot on the same page ("#rook-ranked") are scrolled here and the address is left alone:
  // on collection pages the theme takes an address change for a filter change and jumps to the product grid.
  document.addEventListener('click', (event) => {
    const link = event.target.closest ? event.target.closest('.rook-about a[href^="#"]') : null;
    if (!link) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  const inEditor = window.Shopify && window.Shopify.designMode;
  if (reduceMotion || inEditor || !('IntersectionObserver' in window)) return;

  const root = document.documentElement;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const all = (selector) => Array.from(document.querySelectorAll(selector));

  root.classList.add('rook-about-js');

  const init = () => {
    if (!document.querySelector('.rook-about')) {
      root.classList.remove('rook-about-js');
      return;
    }

    // statement: one span per word
    const statements = all('.rook-about [data-ra-words]').map((el) => {
      const words = el.textContent.trim().split(/\s+/);
      el.setAttribute('aria-label', words.join(' '));
      el.textContent = '';
      const spans = words.map((word, index) => {
        const span = document.createElement('span');
        span.className = 'rook-about-w';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = word;
        el.appendChild(span);
        if (index < words.length - 1) el.appendChild(document.createTextNode(' '));
        return span;
      });
      return { el, spans };
    });

    const drifts = all('.rook-about [data-ra-drift]');
    const strips = all('.rook-about [data-ra-slide]');

    // figures: a plain number counts up from zero
    const count = (el) => {
      const raw = (el.dataset.raCount || '').trim();
      if (!/^\d+(\.\d+)?$/.test(raw)) return;
      const target = parseFloat(raw);
      const decimals = (raw.split('.')[1] || '').length;
      const started = performance.now();
      const duration = 1500;
      const step = (now) => {
        const progress = clamp((now - started) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = (target * eased).toFixed(decimals);
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = raw;
      };
      requestAnimationFrame(step);
    };

    const update = () => {
      const vh = window.innerHeight;
      statements.forEach(({ el, spans }) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -vh || rect.top > vh * 2) return;
        // starts as the text comes up into view, done a little before it reaches the middle of the screen
        const progress = clamp((vh * 0.86 - rect.top) / (rect.height + vh * 0.3), 0, 1);
        const filled = progress * spans.length;
        spans.forEach((span, index) => {
          span.style.opacity = (0.14 + 0.86 * clamp(filled - index, 0, 1)).toFixed(3);
        });
      });
      drifts.forEach((el) => {
        const rect = el.parentElement.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const offset = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        el.style.setProperty('--ra-drift', clamp(offset, -1, 1).toFixed(3));
      });
      strips.forEach((track) => {
        const box = track.parentElement.getBoundingClientRect();
        if (box.bottom < -200 || box.top > vh + 200) return;
        const extra = track.scrollWidth - box.width;
        if (extra <= 0) {
          track.style.transform = '';
          return;
        }
        // from the strip entering at the bottom of the screen to its middle nearing the top
        const progress = clamp((vh - box.top) / (vh * 0.9 + box.height * 0.5), 0, 1);
        track.style.transform = `translate3d(${(-extra * progress).toFixed(1)}px, 0, 0)`;
      });
    };

    let queued = false;
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          observer.unobserve(el);
          el.classList.add('is-in');
          el.querySelectorAll('[data-ra-count]').forEach(count);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    all('.rook-about [data-ra-reveal], .rook-about [data-ra-clip]').forEach((el) => observer.observe(el));
  };

  // wait for the page loader to open, so the first entrances are not played behind it
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    init();
  };
  const whenShown = () => {
    const loader = document.querySelector('[class*="page-transition"]');
    if (!loader || loader.classList.contains('fade-out')) return start();
    const watcher = new MutationObserver(() => {
      if (!loader.classList.contains('fade-out')) return;
      watcher.disconnect();
      window.setTimeout(start, 250);
    });
    watcher.observe(loader, { attributes: true, attributeFilter: ['class'] });
    window.setTimeout(() => {
      watcher.disconnect();
      start();
    }, 3500);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whenShown);
  else whenShown();
})();
